<script>
import { scanEmMassa } from '../../../../domain/automacoes/services'

const EXTENSOES_PLANILHA_PERMITIDAS = ['xlsx', 'xls']
const TAMANHO_MAXIMO_PLANILHA_BYTES = 5 * 1024 * 1024 // 5MB, mesmo limite validado na API
const TAMANHO_MAXIMO_PDF_BYTES = 20 * 1024 * 1024 // 20MB, mesmo limite validado na API

export default {
  name: 'ScanEmMassa',
  data () {
    return {
      planilha: null,
      pdf: null,
      tipo: 'scan_grv',
      loading: false,
      resultado: null // resposta da API (preview ou aplicado)
    }
  },
  computed: {
    prontoParaAnalisar () {
      return !!this.planilha && !!this.pdf
    },
    temPreview () {
      return !!this.resultado && !this.resultado.aplicado
    },
    temResultadoAplicado () {
      return !!this.resultado && this.resultado.aplicado
    },
    podeConfirmar () {
      return this.temPreview && this.resultado.totalAtualizadas > 0
    }
  },
  methods: {
    extensao (nome) {
      return (nome.split('.').pop() || '').toLowerCase()
    },
    escolherPlanilha () {
      if (this.loading) return
      this.$refs.inputPlanilha.click()
    },
    escolherPdf () {
      if (this.loading) return
      this.$refs.inputPdf.click()
    },
    onPlanilhaChange (e) {
      const file = e.target.files[0]
      if (this.$refs.inputPlanilha) this.$refs.inputPlanilha.value = ''
      if (!file) return

      if (!EXTENSOES_PLANILHA_PERMITIDAS.includes(this.extensao(file.name))) {
        alert('Formato de arquivo inválido. Envie um .xlsx ou .xls.')
        return
      }
      if (file.size > TAMANHO_MAXIMO_PLANILHA_BYTES) {
        alert('Planilha muito grande. Limite de 5MB.')
        return
      }

      this.resultado = null
      this.planilha = file
    },
    onPdfChange (e) {
      const file = e.target.files[0]
      if (this.$refs.inputPdf) this.$refs.inputPdf.value = ''
      if (!file) return

      if (this.extensao(file.name) !== 'pdf') {
        alert('O arquivo de scan deve ser um PDF.')
        return
      }
      if (file.size > TAMANHO_MAXIMO_PDF_BYTES) {
        alert('PDF muito grande. Limite de 20MB.')
        return
      }

      this.resultado = null
      this.pdf = file
    },
    trocarArquivos () {
      this.planilha = null
      this.pdf = null
      this.resultado = null
    },
    async analisar () {
      await this.enviar(false)
    },
    async confirmar () {
      if (!this.podeConfirmar) return
      const confirma = confirm(
        `Confirma o envio do scan para ${this.resultado.totalAtualizadas} GRV(s)? Esta ação não pode ser desfeita automaticamente.`
      )
      if (!confirma) return
      await this.enviar(true)
    },
    async enviar (aplicar) {
      if (!this.prontoParaAnalisar || this.loading) return
      this.loading = true
      this.$loaderOverlay.open().message(aplicar ? 'Vinculando scan...' : 'Analisando planilha...')
      try {
        const formData = new FormData()
        formData.append('planilha', this.planilha)
        formData.append('pdf', this.pdf)
        formData.append('tipo', this.tipo)
        formData.append('aplicar', aplicar ? '1' : '0')

        const { data } = await scanEmMassa(formData)
        this.resultado = data
        if (aplicar) {
          this.$emit('finishSubmit')
        }
      } catch (response) {
        window.responseError(response)
        console.log(response)
      } finally {
        this.loading = false
        this.$loaderOverlay.hide()
      }
    },
    corStatus (status) {
      if (status === 'ok') return 'text-success'
      if (status === 'erro') return 'text-danger'
      return 'text-muted'
    },
    iconeStatus (status) {
      if (status === 'ok') return 'fa-check'
      if (status === 'erro') return 'fa-times'
      return 'fa-minus'
    }
  }
}
</script>

<template>
  <div class="wrapper-md">
    <p class="text-muted">
      Envie um único PDF de scan e uma planilha (.xlsx ou .xls) para vincular esse mesmo arquivo a várias GRVs de
      uma só vez. A planilha deve ter uma linha de cabeçalho com a coluna:
    </p>
    <ul class="text-muted">
      <li><strong>GRV</strong> (ou <strong>numero</strong>) — obrigatória, número da GRV</li>
    </ul>
    <p class="text-muted">
      GRVs que já tiverem um arquivo desse tipo são ignoradas (não sobrescreve). Limite de 5MB para a planilha,
      20MB para o PDF e 2000 linhas por envio. Somente pátios aos quais você tem acesso podem ser alterados.
    </p>

    <div class="form-group" v-if="!resultado">
      <label>Tipo de documento</label>
      <select class="form-control" v-model="tipo" :disabled="loading" style="max-width: 300px">
        <option value="scan_grv">Scan GRV</option>
        <option value="unificado">Documentos Unificados</option>
      </select>
    </div>

    <div class="m-b" v-if="!planilha || !pdf">
      <button type="button" class="btn btn-default m-r-sm" @click="escolherPlanilha" :disabled="loading">
        <i class="fa fa-upload m-r-xs"></i> {{ planilha ? 'Trocar planilha' : 'Selecionar planilha' }}
      </button>
      <button type="button" class="btn btn-default" @click="escolherPdf" :disabled="loading">
        <i class="fa fa-file-pdf-o m-r-xs"></i> {{ pdf ? 'Trocar PDF' : 'Selecionar PDF de scan' }}
      </button>
      <input
        type="file"
        ref="inputPlanilha"
        v-show="false"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        @change="onPlanilhaChange"
      >
      <input
        type="file"
        ref="inputPdf"
        v-show="false"
        accept=".pdf,application/pdf"
        @change="onPdfChange"
      >
      <div class="m-t-sm text-muted">
        <div v-if="planilha"><i class="fa fa-file-excel-o m-r-xs"></i> {{ planilha.name }}</div>
        <div v-if="pdf"><i class="fa fa-file-pdf-o m-r-xs"></i> {{ pdf.name }}</div>
      </div>
    </div>

    <div class="m-b" v-else>
      <span><i class="fa fa-file-excel-o m-r-xs"></i> {{ planilha.name }}</span> —
      <span><i class="fa fa-file-pdf-o m-r-xs"></i> {{ pdf.name }}</span>
      <button type="button" class="btn btn-xs btn-link" @click="trocarArquivos" :disabled="loading">
        (trocar arquivos)
      </button>
      <div class="m-t-sm" v-if="!resultado">
        <button type="button" class="btn btn-primary" @click="analisar" :disabled="loading">
          <i class="fa fa-search m-r-xs"></i> Analisar planilha (pré-visualização)
        </button>
      </div>
    </div>

    <div v-if="resultado">
      <div class="alert" :class="temResultadoAplicado ? 'alert-success' : 'alert-info'">
        <strong>{{ temResultadoAplicado ? 'Scan vinculado.' : 'Pré-visualização (nada foi gravado ainda).' }}</strong>
        <br>
        Total de linhas: {{ resultado.totalLinhas }} —
        <span class="text-success">{{ resultado.totalAtualizadas }} para vincular</span> —
        <span class="text-danger">{{ resultado.totalErros }} com erro</span>
      </div>

      <div class="table-responsive" style="max-height: 45vh; overflow-y: auto;">
        <table class="table table-hover table-striped table-condensed b-t b-light">
          <thead>
          <tr>
            <th>Linha</th>
            <th>GRV</th>
            <th>Status</th>
            <th>Mensagem</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="linha in resultado.linhas" :key="linha.linha">
            <td>{{ linha.linha }}</td>
            <td>{{ linha.numero }}</td>
            <td :class="corStatus(linha.status)">
              <i class="fa" :class="iconeStatus(linha.status)"></i> {{ linha.status }}
            </td>
            <td>{{ linha.mensagem }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="m-t" v-if="temPreview">
        <button type="button" class="btn btn-success" @click="confirmar" :disabled="!podeConfirmar || loading">
          <i class="fa fa-check m-r-xs"></i> Confirmar e vincular em {{ resultado.totalAtualizadas }} GRV(s)
        </button>
        <span class="text-muted m-l-sm" v-if="!podeConfirmar">Nenhuma linha válida para aplicar.</span>
      </div>

      <div class="m-t" v-if="temResultadoAplicado">
        <button type="button" class="btn btn-default" @click="$emit('close')">Fechar</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>



// WEBPACK FOOTER //
// ScanEmMassa.vue?1ad91b71